/**
 * „Was ist neu“ — der rote Punkt und der Stand, den er kennt.
 *
 * ## Warum es diese Datei gibt
 *
 * Der Changelog steht seit dem 20.8.2026 in der Hilfe, und niemand hat ihn je
 * gesehen: Man kommt nur dorthin, wenn man die Hilfe aufschlägt, und man
 * schlägt die Hilfe nur auf, wenn man etwas sucht. Neuerungen sind aber genau
 * das, was man **nicht** sucht — man weiß ja nicht, dass es sie gibt.
 *
 * Der Punkt in der Seitenleiste ist die Antwort darauf, und er ist die
 * kleinste mögliche: ein Vergleich zweier Daten. Das jüngste Datum im
 * Changelog gegen das Datum, das beim letzten Öffnen gemerkt wurde. Kein
 * Zähler je Eintrag, keine Liste gelesener Kennungen — ein Mensch liest so
 * eine Seite von oben, und was er dabei überspringt, will er nicht noch
 * einmal angeboten bekommen.
 *
 * ## Warum der Stand im Browser liegt und nicht am Konto
 *
 * Weil er nichts kostet, wenn er verlorengeht: Im schlimmsten Fall sieht
 * jemand den Punkt an einem zweiten Gerät noch einmal und schlägt eine Seite
 * auf, die er kennt. Ein Feld am Konto verlangte eine Spalte, eine Wanderung
 * und einen Endpunkt für eine Auskunft, die eine Zeile Text ist.
 *
 * **Reine Funktionen** — das Rechnen kennt keinen Speicher. Die beiden
 * Speicherzugriffe stehen unten und nehmen ihren Speicher als Argument, damit
 * sie ohne Browser prüfbar bleiben.
 */

/** Der Schlüssel im `localStorage`. An einer Stelle — Lesen und Schreiben dürfen nicht auseinanderlaufen. */
export const NEU_SCHLUESSEL = 'reise.neu.gelesen';

/** Der Anker des Changelog-Abschnitts in der Hilfe — siehe `hilfe/gliederung.ts`. */
export const NEU_ANKER = 'was-neu-ist';

/**
 * Das Kleinste, was hier gebraucht wird — `localStorage` erfüllt es.
 *
 * Ein eigener Typ und nicht `Storage`: Der Test reicht zwei Zeilen Attrappe
 * herein, und `Storage` verlangte `length`, `key()`, `clear()` und
 * `removeItem()` dazu — vier Methoden, die niemand ruft.
 */
export interface Speicher {
  getItem(schluessel: string): string | null;
  setItem(schluessel: string, wert: string): void;
}

/**
 * Gibt es etwas Ungelesenes?
 *
 * `true` nur, wenn es ein jüngstes Datum gibt **und** es neuer ist als der
 * gemerkte Stand. Ohne gemerkten Stand ist alles neu — das ist die richtige
 * Richtung: Wer die Anwendung zum ersten Mal öffnet, soll erfahren, dass es
 * die Seite gibt.
 *
 * Verglichen werden `JJJJ-MM-TT`-Zeichenketten, und das ist Absicht: In dieser
 * Form ist die alphabetische Ordnung die zeitliche. Ein `Date` daraus zu bauen
 * hieße, eine Zeitzone zu erfinden, die in der Frage nicht vorkommt.
 */
export function istUngelesen(
  juengstes: string | null | undefined,
  gelesen: string | null | undefined
): boolean {
  if (!juengstes) return false;
  if (!gelesen) return true;
  return juengstes > gelesen;
}

/**
 * Der gemerkte Stand — `null`, wenn keiner dasteht oder er nicht lesbar ist.
 *
 * Alles in `try`: In einem privaten Fenster wirft schon der Zugriff auf
 * `localStorage`, und ein roter Punkt ist kein Grund, eine Fläche abstürzen zu
 * lassen. Ein unlesbarer Wert wird wie „nie gelesen“ behandelt.
 */
export function gelesenStand(speicher: Speicher | null | undefined): string | null {
  try {
    const roh = speicher?.getItem(NEU_SCHLUESSEL) ?? null;
    return roh && /^\d{4}-\d{2}-\d{2}$/.test(roh) ? roh : null;
  } catch {
    return null;
  }
}

/**
 * Den Stand merken — beim Öffnen der Seite, nicht beim Lesen des Eintrags.
 *
 * Gemerkt wird das **jüngste** Datum und nicht der heutige Tag: Sonst
 * verschluckte ein Klick am 14.9. einen Eintrag, der am 14.9. abends noch
 * dazukommt.
 */
export function alsGelesenMerken(
  speicher: Speicher | null | undefined,
  juengstes: string | null | undefined
): void {
  if (!juengstes) return;
  try {
    speicher?.setItem(NEU_SCHLUESSEL, juengstes);
  } catch {
    // Kein Speicher, kein Merken. Der Punkt steht dann beim nächsten Öffnen
    // wieder da — unangenehm, aber ehrlich, und die Seite geht trotzdem auf.
  }
}
